import * as React from "react";
import {
  calculateNextRoute,
  shouldInitializePaywall,
} from "./logic/paywall-calculations";
import { usePaywallIframeManager } from "../context/paywalliframeManager.context";
import { usePaywallStateManagerProvider } from "../context/paywallStateManager.context";
import { Resource } from "../../types";
import { useParentIframe } from "@trello-powerup-monorepo/iframe-messenger-v2";

export interface PaywallRouteGuardProps {
  getCurrentLocation: () => string;
  onNavigate: (path: string, options?: { replace?: boolean }) => void;
  resources: Resource[];
  appId: string;
  credentials: Record<string, string>;

  //routing
  protectedPath: string;
  profilePath: string;
  authorizePath: string;
  createProfilePath: string;
  noAccessPath: string;
}

// AIDEV-NOTE: parallel-loading - PaywallGuard now enables immediate data fetching with secure overlay

export const PaywallRouteGuard = ({
  children,
  getCurrentLocation,
  onNavigate,
  resources = [],
  appId,
  credentials,
  protectedPath,
  profilePath,
  authorizePath,
  createProfilePath,
  noAccessPath,
}: React.PropsWithChildren<PaywallRouteGuardProps>) => {
  const { isReady, sendIframeInitialization } = usePaywallIframeManager();
  const { securityStep } = usePaywallStateManagerProvider();
  const { debug } = useParentIframe();

  const log = (message: string, context?: unknown) => {
    if (!debug) return;
    const debugFn = debug as (message: string, ...args: unknown[]) => void;
    const prefixed = `[PaywallParent] ${message}`;
    if (context !== undefined) {
      debugFn(prefixed, context);
    } else {
      debugFn(prefixed);
    }
  };

  // Validate that required location dependencies are provided
  if (!getCurrentLocation || !onNavigate) {
    throw new Error(
      "PaywallGuard requires getCurrentLocation and onNavigate props when used without react-router"
    );
  }

  // Orchestrate paywall initialization
  React.useEffect(() => {
    // Use pure function to determine if initialization should occur
    const shouldInitialize = shouldInitializePaywall(
      isReady,
      credentials,
      resources
    );

    // Orchestrate: decide when and how to initialize
    if (shouldInitialize && credentials) {
      log("Sending IFRAME_INITIALIZE to paywall child", {
        resourceCount: resources.length,
        hasCredentials: Boolean(credentials),
      });
      sendIframeInitialization({
        credentials,
        appId,
        resources,
      });
      return;
    }

    if (isReady) {
      log("Initialization skipped", {
        hasCredentials: Boolean(credentials),
        resourceCount: resources.length,
        isReady,
      });
    }
  }, [
    isReady,
    resources,
    credentials,
    appId,
    sendIframeInitialization,
    debug,
  ]);

  // Orchestrate navigation based on security step
  React.useEffect(() => {
    if (!isReady) return;

    const currentLocation = getCurrentLocation();
    const paths = {
      protected: protectedPath,
      profile: profilePath,
      authorize: authorizePath,
      createProfile: createProfilePath,
      noAccess: noAccessPath,
    };
    const hasResources = resources.length > 0;

    // Use pure function to calculate next route
    const nextRoute = calculateNextRoute(
      securityStep,
      currentLocation,
      paths,
      hasResources
    );

    // Orchestrate: decide when and how to navigate
    if (nextRoute) {
      log("Routing iframe parent", {
        securityStep,
        currentLocation,
        nextRoute,
      });
      onNavigate(nextRoute, { replace: true });
    }
  }, [
    isReady,
    securityStep,
    onNavigate,
    getCurrentLocation,
    protectedPath,
    profilePath,
    authorizePath,
    createProfilePath,
    noAccessPath,
    resources,
    debug,
  ]);

  return <>{children}</>;
};

// AIDEV-NOTE: WithNestedRoutes removed - router-dependent functionality should be handled by consuming app
